import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ReportDateRangeComponent } from '../report-date-range/report-date-range.component';
import { ReportDateRangeService } from '../../report-date-range.service';
import { AppDataService } from '../../core/services/app-data.service';
import { ReportService } from '../../core/services/report.service';
import { ExportService } from '../../core/services/export.service';
import { environment } from '../../environments/environment';

interface ReportLink {
  route: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, RouterLink, ReportDateRangeComponent],
  templateUrl: './reports.component.html',
  styleUrl: '../../report-shared.css',
})
export class ReportsComponent implements OnInit {
  protected dateRange = inject(ReportDateRangeService);
  private readonly appData = inject(AppDataService);
  private readonly reportsService = inject(ReportService);
  private readonly exporter = inject(ExportService);

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  reports: ReportLink[] = [
    {
      route: '/stock-on-hand',
      title: 'Stock on Hand',
      description: 'Current inventory levels by category',
      icon: 'bi-box-seam',
      color: '#4f6ef7',
    },
    {
      route: '/low-stock',
      title: 'Low Stock',
      description: 'Items at or below reorder level',
      icon: 'bi-exclamation-triangle',
      color: '#ef4444',
    },
    {
      route: '/stock-movement',
      title: 'Stock Movement',
      description: 'Incoming and outgoing for the selected period',
      icon: 'bi-arrow-left-right',
      color: '#4f6ef7',
    },
    {
      route: '/case-usage',
      title: 'Case / Job Usage',
      description: 'Items issued per case or job this period',
      icon: 'bi-calendar3',
      color: '#8b5cf6',
    },
    {
      route: '/checked-out-equipment',
      title: 'Checked-Out Equipment',
      description: 'Assets currently out on rental',
      icon: 'bi-clipboard-check',
      color: '#10b981',
    },
    {
      route: '/overdue-returns-report',
      title: 'Overdue Returns',
      description: 'Rentals past their due date',
      icon: 'bi-clock-history',
      color: '#ef4444',
    },
    {
      route: '/damaged-equipment',
      title: 'Damaged Equipment',
      description: 'Assets in repair, maintenance, or written off',
      icon: 'bi-tools',
      color: '#f59e0b',
    },
    {
      route: '/stock-valuation',
      title: 'Stock Valuation',
      description: 'Total inventory value by category',
      icon: 'bi-coin',
      color: '#f59e0b',
    },
  ];

  exportAll(): void {
    const range = {
      startDate: this.dateRange.appliedStartDate(),
      endDate: this.dateRange.appliedEndDate(),
    };

    const payload = {
      range,
      stockOnHand: this.reportsService.getStockOnHandSummary(range),
      stockOnHandByCategory:
        this.reportsService.getStockOnHandByCategory(range),
      lowStock: this.reportsService.getLowStockReport(range),
      stockMovement: this.reportsService.getStockMovement(range),
      caseUsage: this.reportsService.getCaseUsage(range),
      checkedOutEquipment:
        this.reportsService.getCheckedOutEquipmentReport(range),
      overdueReturns: this.reportsService.getOverdueReturnsReport(range),
      damagedEquipment: this.reportsService.getDamagedEquipmentReport(range),
      stockValuation: this.reportsService.getStockValuation(range),
    };

    this.exporter.downloadJson(
      `reports-all-${new Date().toISOString().slice(0, 10)}`,
      payload,
    );
  }
}
